'use client';

import { useState } from 'react';

export interface StoneFAQItem {
  question: string;
  answer: string;
}

export interface StoneFAQProps {
  faqs: StoneFAQItem[];
  title?: string;
  description?: string;
  locale?: string;
  className?: string;
}

/**
 * Stone-styled FAQ accordion with FAQPage JSON-LD for GEO.
 * Answers use data-speakable and .faq-answer for AI/voice extraction.
 */
export function StoneFAQ({
  faqs,
  title,
  description,
  locale = 'en',
  className = '',
}: StoneFAQProps) {
  const [openItems, setOpenItems] = useState<Set<number>>(new Set([0]));

  if (!faqs.length) {
    return null;
  }

  const copy =
    locale === 'fr'
      ? {
          title: title ?? 'Questions fréquentes',
          description:
            description ??
            'Réponses claires aux questions les plus posées par nos clients.',
        }
      : {
          title: title ?? 'Frequently Asked Questions',
          description:
            description ??
            'Clear answers to the questions buyers ask us most often.',
        };

  const toggleItem = (index: number) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <section className={`faq-section py-16 md:py-20 ${className}`} aria-labelledby="stone-faq-heading">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="container mx-auto max-w-3xl px-6 md:px-12">
        <div className="mb-10 md:mb-12 text-center">
          <h2
            id="stone-faq-heading"
            className="font-serif text-3xl md:text-5xl text-stone-900 italic mb-4 leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {copy.title}
          </h2>
          {copy.description ? (
            <p className="font-sans text-stone-600 font-light leading-relaxed max-w-xl mx-auto">
              {copy.description}
            </p>
          ) : null}
        </div>

        <div className="divide-y divide-stone-200 border-y border-stone-200">
          {faqs.map((faq, index) => {
            const isOpen = openItems.has(index);
            const answerId = `stone-faq-answer-${index}`;

            return (
              <div key={`faq-${index}-${faq.question.slice(0, 24)}`}>
                <button
                  type="button"
                  className="w-full py-5 md:py-6 text-left flex items-start justify-between gap-6 group"
                  onClick={() => toggleItem(index)}
                  aria-expanded={isOpen}
                  aria-controls={answerId}
                >
                  <h3 className="font-sans text-base md:text-lg text-stone-900 font-medium leading-snug pr-2 group-hover:text-stone-700 transition-colors">
                    {faq.question}
                  </h3>
                  <span
                    className={`mt-1 flex-shrink-0 text-stone-400 transition-transform duration-200 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                    aria-hidden
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </button>

                <div
                  id={answerId}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-[40rem] opacity-100 pb-6' : 'max-h-0 opacity-0'
                  }`}
                  aria-hidden={!isOpen}
                >
                  <p
                    className="faq-answer font-sans text-stone-600 font-light leading-relaxed"
                    data-speakable
                  >
                    {faq.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default StoneFAQ;
